import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { MdOutlineClose } from 'react-icons/md';

/**
 * Drawer component — slide-in side panel rendered via React portal.
 *
 * Props:
 *   show          {bool}    – controls visibility
 *   onHide        {func}    – called when backdrop or close button clicked
 *   title         {node}    – header title (string or JSX)
 *   children      {node}    – body content (scrollable)
 *   footer        {node}    – footer content; omit to hide footer
 *   placement     {string}  – 'right' | 'left'  (default: 'right')
 *   width         {string}  – CSS width of the panel (default: '50%')
 *   minWidth      {string}  – CSS min-width of the panel (default: '420px').
 *                              Clamped to the viewport at render time, so the
 *                              panel never ends up wider than the screen.
 *   backdropClick {bool}    – close on backdrop click (default: true)
 *   className     {string}  – extra classes on the panel div
 */
const Drawer = ({
  show,
  onHide,
  title,
  children,
  footer,
  placement = 'right',
  width = '50%',
  minWidth = '420px',
  backdropClick = true,
  className = '',
}) => {
  // Lock the background page scroll while the drawer is open, otherwise the
  // page's own scrollbar stays visible next to the drawer's (double scrollbar).
  // Both html and body are locked: depending on the page the scroll container is
  // one or the other, and locking only body leaves the html scrollbar showing.
  useEffect(() => {
    if (!show) return undefined;

    const { body, documentElement: html } = document;
    const prev = { body: body.style.overflow, html: html.style.overflow };

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      body.style.overflow = prev.body;
      html.style.overflow = prev.html;
    };
  }, [show]);

  if (!show) return null;

  const positionClass = placement === 'left' ? 'tw-left-0' : 'tw-right-0';

  // `minWidth` is a floor meant for desktop. Taken literally it made the panel
  // wider than a phone (the 420px default against a 390px iPhone viewport),
  // pushing part of the form off-screen with no way to scroll to it — and 41 of
  // the ~86 call sites rely on that default. Clamping to 100vw keeps the floor
  // on wide screens and drops it to a full-width sheet on narrow ones.
  // Numbers are accepted because some call sites pass them (React would append
  // `px` itself, but `min()` needs the unit spelled out).
  const asLength = (v) => (typeof v === 'number' ? `${v}px` : v);
  const panelStyle = {
    width: asLength(width),
    minWidth: `min(${asLength(minWidth)}, 100vw)`,
    maxWidth: '100vw',
    // `dvh`, not `h-full`/`100vh`: on mobile Safari `vh` measures the taller
    // URL-bar-retracted viewport, which put the drawer's footer buttons under
    // the browser chrome.
    height: '100dvh',
  };

  const handleBackdropClick = () => {
    if (backdropClick) onHide();
  };

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="tw-fixed tw-inset-0 tw-bg-slate-900/20 tw-backdrop-blur-[1px] tw-z-[1190]"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`tw-fixed tw-top-0 ${positionClass} tw-bg-white tw-z-[1200] tw-shadow-2xl tw-flex tw-flex-col ${className}`}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        {title !== undefined && (
          <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-flex-shrink-0">
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-700 tw-font-semibold tw-text-sm">
              {title}
            </div>
            <button
              onClick={onHide}
              className="ptl-tap tw-p-1.5 tw-rounded-md tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-200 tw-transition-colors tw-cursor-pointer tw-border-0 tw-bg-transparent"
              aria-label="Close"
            >
              <MdOutlineClose size={18} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-px-6 tw-py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-px-6 tw-py-4 tw-border-t tw-border-slate-200 tw-bg-slate-50 tw-flex-shrink-0"
            // Extra bottom room for the iOS home indicator when installed as a
            // PWA; --safe-bottom is 0px everywhere else.
            style={{ paddingBottom: 'calc(1rem + var(--safe-bottom))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(content, document.body);
};

Drawer.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.node,
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  placement: PropTypes.oneOf(['right', 'left']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  backdropClick: PropTypes.bool,
  className: PropTypes.string,
};

export default Drawer;
